import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AppData } from '../types';
import { onDataChange, initializeDb, clearDataCache } from '../services/firestoreService';
import { useAuthContext } from './AuthContext';
import { safeLocalStorage } from '../utils/safeStorage';

/**
 * AppDataContext
 * 
 * Wraps the existing `onDataChange` listener from firestoreService.ts
 * to provide global app data via React Context, eliminating prop drilling.
 * 
 * Phase 5 upgrade: Individual data hooks (useParticipants, useSubmissions) will
 * consume from this context and add pagination — no consumer changes required.
 * 
 * Phase 6 upgrade: The projects listener will gain an `organizationId` filter
 * clause based on the user's JWT Claims.
 */

interface AppDataContextValue {
    appData: AppData | null;
    setAppData: React.Dispatch<React.SetStateAction<AppData | null>>;
    isLoadingData: boolean;
    currentProjectId: string;
    setCurrentProjectId: (id: string) => void;
}

const PROJECT_ID_KEY = 'dekko_current_project_id';

const APPDATA_DEFAULT: AppDataContextValue = {
    appData: null,
    setAppData: () => {},
    isLoadingData: true,
    currentProjectId: '',
    setCurrentProjectId: () => {},
};

// Singleton: one context object even if Vite evaluates this module twice
const APPDATA_CTX_KEY = '__DEKKO_APPDATA_CTX__';
const AppDataContext: React.Context<AppDataContextValue> =
    (window as any)[APPDATA_CTX_KEY] ||
    ((window as any)[APPDATA_CTX_KEY] = createContext<AppDataContextValue>(APPDATA_DEFAULT));

export const AppDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user, userProfile, claimsRole, claimsOrgId, claimsKnown } = useAuthContext();
    const [appData, setAppData] = useState<AppData | null>(null);
    const [isLoadingData, setIsLoadingData] = useState(true);

    const [currentProjectId, setCurrentProjectId] = useState<string>(() => {
        return safeLocalStorage.getItem(PROJECT_ID_KEY) || '';
    });

    // Persist project selection
    useEffect(() => {
        if (currentProjectId) {
            safeLocalStorage.setItem(PROJECT_ID_KEY, currentProjectId);
        }
    }, [currentProjectId]);

    // Initialize Firebase data listeners when user is authenticated
    useEffect(() => {
        if (!user) {
            setAppData(null);
            setIsLoadingData(false);
            clearDataCache(); // drop the stale-while-revalidate seed on sign-out
            return;
        }

        // Wait until JWT Claims are fetched in useAuth
        // to avoid querying Firestore with orgId=null
        if (!claimsRole) {
            // Claims KNOWN and absent = genuinely unprovisioned, not in flight.
            // Settle the flag so the UI can show an honest screen instead of
            // holding a loader forever (there is no data to load for this user).
            if (claimsKnown) setIsLoadingData(false);
            return;
        }

        let isSubscribed = true;
        let gotData = false;
        let unsubscribe: (() => void) | undefined;
        let loadingTimeout: NodeJS.Timeout;

        // Re-gate loading whenever listeners are (re)established with nothing
        // to show yet. Without this the logged-out run's setIsLoadingData(false)
        // sticks across an in-session login, which both let App gates fall
        // through AND disabled the timeout below (its old `isLoadingData`
        // guard captured a stale `false` from this closure) — the indefinite
        // splash hang. Keep prev when appData already exists to avoid a
        // flash-back to loading on role/org changes.
        setIsLoadingData(prev => (appData ? prev : true));

        const setupListener = async () => {
            try {
                await initializeDb();

                loadingTimeout = setTimeout(() => {
                    if (isSubscribed && !gotData) {
                        console.warn('Data loading timed out, forcing UI render');
                        setIsLoadingData(false);
                        setAppData(prev => prev || {
                            projects: [],
                            participants: [],
                            submissions: [],
                            tasks: [],
                            participantGroups: [],
                            teamMembers: [],
                            invitations: [],
                            participantInvitations: [],
                            users: [],
                            nudges: []
                        });
                    }
                }, 10000);

                if (isSubscribed) {
                    unsubscribe = onDataChange((data) => {
                        if (isSubscribed) {
                            gotData = true;
                            setAppData(data);
                            setIsLoadingData(false);
                            clearTimeout(loadingTimeout);
                        }
                    }, claimsRole === 'superadmin' ? null : claimsOrgId, {
                        // Participant clients get own-data-scoped queries —
                        // org-wide queries are denied by firestore.rules.
                        role: claimsRole,
                        uid: user.uid,
                        projectId: userProfile?.projectId,
                        email: user.email || undefined,
                    });
                }
            } catch (error) {
                console.error("Error setting up data listeners:", error);
                if (isSubscribed) {
                    setIsLoadingData(false);
                    // Settle appData too. If initializeDb() threw, the 10s
                    // fallback above was never armed, so leaving appData null
                    // strands every consumer that treats "no appData yet" as
                    // still-loading (see isDataReady) on a permanent loader.
                    setAppData(prev => prev || {
                        projects: [],
                        participants: [],
                        submissions: [],
                        tasks: [],
                        participantGroups: [],
                        teamMembers: [],
                        invitations: [],
                        participantInvitations: [],
                        users: [],
                        nudges: []
                    });
                }
            }
        };

        setupListener();

        return () => {
            isSubscribed = false;
            if (unsubscribe) unsubscribe();
            if (loadingTimeout) clearTimeout(loadingTimeout);
        };
    // userProfile?.projectId in deps: a participant's scoped listeners can
    // only be established once their project is known — re-run after join.
    // claimsKnown: the !claimsRole branch above reads it to settle
    // isLoadingData once claims are known absent — without it in the deps,
    // that branch only re-runs when claimsRole/claimsOrgId/user change, so
    // claimsKnown flipping true on its own leaves isLoadingData stuck true
    // (masked today only by the `!user` mount run settling it first).
    }, [user, claimsRole, claimsOrgId, claimsKnown, claimsRole === 'participant' ? userProfile?.projectId : null]);

    // Auto-select the correct project based on user role
    useEffect(() => {
        if (!appData) return;

        // For participants, use their assigned project from userProfile
        if (claimsRole === 'participant' && userProfile?.projectId) {
            const assignedProject = appData.projects.find(p => p.id === userProfile.projectId);
            if (assignedProject) {
                setCurrentProjectId(userProfile.projectId);
                return;
            }
        }

        // For researchers — restore saved preference only; don't auto-select
        // so the ProjectChooser screen can be shown when there's no preference
        const savedProjectId = safeLocalStorage.getItem(PROJECT_ID_KEY);
        if (savedProjectId) {
            const validProject = appData.projects.find(p => p.id === savedProjectId);
            if (validProject) {
                setCurrentProjectId(savedProjectId);
            } else if (appData.projects.length > 0) {
                // Saved project was deleted — clear stale preference.
                // Only conclude that once projects have actually loaded: this
                // effect re-runs on every data update and projects load COLD
                // (getDocs), so the list is briefly []. Treating that empty
                // window as "deleted" wiped the selection on every reload —
                // bouncing researchers to the chooser and leaving the
                // participant preview with no project, spinning forever.
                safeLocalStorage.removeItem(PROJECT_ID_KEY);
                setCurrentProjectId('');
            }
        }
    }, [appData, userProfile]);

    return (
        <AppDataContext.Provider value={{
            appData,
            setAppData,
            isLoadingData,
            currentProjectId,
            setCurrentProjectId,
        }}>
            {children}
        </AppDataContext.Provider>
    );
};

/**
 * useAppData - access global application data from any component via context.
 * 
 * Usage:
 *   const { appData, currentProjectId, setCurrentProjectId } = useAppData();
 */
export const useAppData = (): AppDataContextValue => {
    return useContext(AppDataContext);
};

export default AppDataContext;
